import type { ExperienceDefinition, ExperienceContext } from '../types'

/**
 * Checks if a specific experience is eligible to run under the current context.
 *
 * @param experience The experience definition to validate
 * @param context The current context solved by the ContextAnalyzer
 * @param options Evaluation options
 */
export function checkEligibility(
  experience: ExperienceDefinition,
  context: ExperienceContext,
  options: { ignoreCooldown?: boolean; disabledIds?: string[] } = {}
): boolean {
  const { id } = experience.metadata
  const { disabledIds = [] } = options

  // 1. Check if disabled globally or by user preferences
  if (disabledIds.includes(id)) {
    return false
  }

  // 2. Evaluate custom logic check from the experience package itself
  try {
    if (!experience.eligibility.check(context)) {
      return false
    }
  } catch (err) {
    console.error(`[EligibilityRules] Error executing check for ${id}:`, err)
    return false
  }

  // 3. Evaluate cooldowns if not ignored (ignored during manual plays / playground)
  if (!options.ignoreCooldown && experience.eligibility.cooldownMs) {
    const historyEntry = context.history[id]
    if (historyEntry && historyEntry.lastRun) {
      const msSinceLastRun = Date.now() - historyEntry.lastRun
      if (msSinceLastRun < experience.eligibility.cooldownMs) {
        return false // Cooldown active
      }
    }
  }

  return true
}
